import React, { useState, useEffect } from "react";
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, IconButton, Box, Typography, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useOutletContext, useNavigate } from 'react-router-dom';
import { OutletContextType } from '../root';
import { arenaManagerInitialize, arenaUpdate, arenaRegister } from '../../api/api';
import {
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';

type Arena = {
    arena_id: string;
    arena_name: string;
    arena_address: string;
    arena_url: string;
    meeting_place: string;
    facility: string;
    isEditing: boolean;
    isRegisterRecord: boolean;
    [key: string]: any;
};

export const ArenaManagement = () => {
  const navigate = useNavigate();
  const { userId } = useOutletContext<OutletContextType>();
  const [reloadTime, setReloadTime] = useState<Date>();
  const [arenas, setArenas] = useState<Arena[]>([]);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [sortBy, setSortBy] = useState<string>("user_id");
  const [openResultDialog, setOpenResultDialog] = useState<boolean>(false);
  const [processResultMessage, setProcessResultMessage] = useState<string>('');

  useEffect(() => {
    // 初期表示処理を行いサーバーサイドから体育館マスタを取得する
    arenaManagerInitialize(userId).then(response => {
      const data = response?.tkgReserve0230DetailsObjectLot?.tkgReserve0230DetailsObjectList?.map((userEntity: any) => ({
        arena_id: userEntity.arenaId,
        arena_name: userEntity.arenaName,
        arena_address: userEntity.arenaAddress,
        arena_url: userEntity.arenaUrl,
        meeting_place: userEntity.meetingPlace,
        facility: userEntity.facility,
        isEditing: false,
        isRegisterRecord: false
      }));
      setArenas(data);
    });
  }, [userId, reloadTime]);

  const handleSort = (column: string) => {
    const isAsc = sortBy === column && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortBy(column);
    const sortedUsers = [...arenas].sort((a, b) => {
      if (a[column] < b[column]) return isAsc ? -1 : 1;
      if (a[column] > b[column]) return isAsc ? 1 : -1;
      return 0;
    });
    setArenas(sortedUsers);
  };

  const handleEdit = (index: number) => {
    const updatedArenas = [...arenas];
    // 編集前のユーザーの情報を保持
    updatedArenas[index] = { ...arenas[index], originalData: { ...arenas[index] } };
    updatedArenas[index].isEditing = true;
    setArenas(updatedArenas);
  };

  const handleSave = (index: number) => {
    const updatedArenas = [...arenas];
    updatedArenas[index].isEditing = false;
    // リクエストを作成
    const requestData = {
        "user_id" : userId,
        "arena_id" : updatedArenas[index].arena_id,
        "arena_name" : updatedArenas[index].arena_name,
        "arena_address" : updatedArenas[index].arena_address,
        "arena_url" : updatedArenas[index].arena_url,
        "meeting_place" : updatedArenas[index].meeting_place,
        "facility" : updatedArenas[index].facility
    }
    if (updatedArenas[index].isRegisterRecord) {
        // 体育館登録APIを呼び出す
        arenaRegister(requestData).then(response => {
          setArenas(updatedArenas);
          setProcessResultMessage(response.message);
          setOpenResultDialog(true);
        });
    } else {
        // 体育館更新APIを呼び出す
        arenaUpdate(requestData).then(response => {
          setArenas(updatedArenas);
          setProcessResultMessage(response.message);
          setOpenResultDialog(true);
        });
    }
  };

  const handleCancel = (index: number) => {
    const updatedArenas = [...arenas];
    if (updatedArenas[index].isRegisterRecord) {
      // レコードを削除する
      updatedArenas.splice(index, 1);
    } else {
      // 編集前のデータに戻す
      updatedArenas[index] = { ...updatedArenas[index].originalData, isEditing: false };
    }
    setArenas(updatedArenas);
  };

  const handleInputChange = (index: number, field: string, value: string) => {
    const updatedArenas = [...arenas];
    updatedArenas[index][field as keyof Arena] = value;
    setArenas(updatedArenas);
  };

  const handleRecordInsert = () => {
    const newArena = {
        arena_id: "",
        arena_name: "",
        arena_address: "",
        arena_url: "",
        meeting_place: "",
        facility: "",
        isEditing: true,
        isRegisterRecord: true
    };
    setArenas([...arenas, newArena]);
  };

  // 表示データがない場合
  if (!arenas || arenas.length === 0) {
    return (
        <Box sx={{ paddingTop:'10vh' }}>
            表示できるデータがありません。
        </Box>
    );
  }

  return (
    <>
    <Box sx={{ paddingTop:'10vh' }}>
      <Button
          variant="outlined"
          color="primary"
          startIcon={<ArrowBackIcon sx={{transition: 'transform 0.3s ease'}} />}
          onClick={() => navigate("/mng/portal")}
          sx={{
            ':hover': {
                backgroundColor: '#1565c0',
                color: 'white'
            }
          }}
      >
          ポータルに戻る
      </Button>
      <TableContainer sx={{ padding: 3 }}>
        <Typography variant="h4" gutterBottom>
          体育館マスタ
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon sx={{transition: 'transform 0.3s ease'}} />}
          onClick={handleRecordInsert}
          sx={{
            ':hover': {
                backgroundColor: '#1565c0',
                color: 'white'
            }
          }}
        >
            レコード追加
        </Button>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell onClick={() => handleSort("arena_id")}>体育館ID</TableCell>
              <TableCell onClick={() => handleSort("arena_name")}>体育館名</TableCell>
              <TableCell onClick={() => handleSort("arena_address")}>住所</TableCell>
              <TableCell onClick={() => handleSort("arena_url")}>体育館URL</TableCell>
              <TableCell onClick={() => handleSort("meeting_place")}>集合場所</TableCell>
              <TableCell onClick={() => handleSort("facility")}>設備</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {arenas.map((arena, index) => (
              <TableRow key={arena.user_id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{arena.arena_id}</TableCell>
                <TableCell>
                    {arena.isEditing ? (
                        <TextField label="体育館名" value={arena.arena_name} onChange={(e) => handleInputChange(index, "arena_name", e.target.value)} />
                    ) : (
                        <span>{arena.arena_name}</span>
                    )}
                </TableCell>
                <TableCell>
                    {arena.isEditing ? (
                        <TextField label="住所" value={arena.arena_address} onChange={(e) => handleInputChange(index, "arena_address", e.target.value)} />
                    ) : (
                        <span>{arena.arena_address}</span>
                    )}
                </TableCell>
                <TableCell>
                    {arena.isEditing ? (
                        <TextField label="体育館URL" value={arena.arena_url} onChange={(e) => handleInputChange(index, "arena_url", e.target.value)} />
                    ) : (
                        <span>{arena.arena_url}</span>
                    )}
                </TableCell>
                <TableCell>
                    {arena.isEditing ? (
                        <TextField label="集合場所" value={arena.meeting_place} onChange={(e) => handleInputChange(index, "meeting_place", e.target.value)} />
                    ) : (
                        <span>{arena.meeting_place}</span>
                    )}
                </TableCell>
                <TableCell>
                    {arena.isEditing ? (
                        <TextField label="設備" value={arena.facility} onChange={(e) => handleInputChange(index, "facility", e.target.value)} />
                    ) : (
                        <span>{arena.facility}</span>
                    )}
                </TableCell>
                <TableCell>
                  {arena.isEditing ? (
                    <>
                      <Button onClick={() => handleSave(index)} variant="contained" color="primary" sx={{ marginRight: 1 }}>
                        保存
                      </Button>
                      <Button onClick={() => handleCancel(index)} variant="outlined" color="secondary">
                        破棄
                      </Button>
                    </>
                  ) : (
                    <IconButton onClick={() => handleEdit(index)}>
                      <Button variant="outlined">編集</Button>
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
    {/* 処理結果通知ダイアログ */}
    <Dialog open={openResultDialog} onClose={() => setOpenResultDialog(false)}>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'primary.main',
            }}
          >
            <InfoIcon fontSize="large" />
            <Typography variant="h6">処理結果</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <pre>{processResultMessage}</pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {setOpenResultDialog(false);setReloadTime(new Date())}} variant="contained" color="primary">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
